import React, { useState } from 'react';
import { Badge } from '../../../ui/badge';

interface ConflictField {
  field: string;
  field_label: string;
  source_value: string | number;
  new_value?: string | number;
  resolved_value: string | number;
  strategy: string;
  sourceAName?: string;
  sourceBName?: string;
}

interface SourceComparisonTabProps {
  conflicts: ConflictField[];
  sources: string[];
  totalMarketValue: number;
  accountsCount: number;
  holdingsCount: number;
  clientIdentifier: string;
  clientAccounts: any[];
  portfolioAccounts: any[];
  clientIdentifierIds: string[];
}

export const SourceComparisonTab: React.FC<SourceComparisonTabProps> = ({
  conflicts, // Reserved for future conflict display
  sources, // Reserved for future source comparison
  totalMarketValue,
  accountsCount,
  holdingsCount,
  clientIdentifier,
  clientAccounts,
  portfolioAccounts,
  clientIdentifierIds
}) => {
  // Suppress unused variable warnings - these may be used in future iterations
  void conflicts;

  // State to track expanded rows
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  // Toggle expand/collapse for a specific row
  const toggleRow = (index: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Use dynamic source names from source_list and format them
  const formatSourceName = (name: string) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  };

  const sourceAName = formatSourceName(sources[0] || 'custodian_a');
  const sourceBName = formatSourceName(sources[1] || 'custodian_b');

  // Extract identifiers from client_identifier_ids
  const sourceAId = clientIdentifierIds[0] || 'CA-50030';
  const sourceBId = clientIdentifierIds[1] || 'CB-80030';

  // Determine prefixes - CA for source A, CB for source B
  const sourceAPrefix = 'CA-'; // First source is always CA-
  const sourceBPrefix = 'CB-'; // Second source is always CB-


  // Calculate source-specific values using client accounts
  const sourceAAccounts = clientAccounts.filter((acc: any) =>
    acc.account_id?.startsWith(sourceAPrefix)
  );
  const sourceBAccounts = clientAccounts.filter((acc: any) =>
    acc.account_id?.startsWith(sourceBPrefix)
  );

  // Calculate market values by source using account amounts
  const sourceAMarketValue = sourceAAccounts.reduce((total: number, acc: any) => {
    return total + (acc.market_value || 0);
  }, 0);

  const sourceBMarketValue = sourceBAccounts.reduce((total: number, acc: any) => {
    return total + (acc.market_value || 0);
  }, 0);

  // Calculate holdings count by source using portfolio accounts
  const sourceAPortfolioAccounts = portfolioAccounts.filter((acc: any) =>
    acc.account_identifier?.startsWith(sourceAPrefix)
  );
  const sourceBPortfolioAccounts = portfolioAccounts.filter((acc: any) =>
    acc.account_identifier?.startsWith(sourceBPrefix)
  );

  const sourceAHoldings = sourceAPortfolioAccounts.reduce((total: number, acc: any) =>
    total + (acc.holdings?.length || 0), 0
  );
  const sourceBHoldings = sourceBPortfolioAccounts.reduce((total: number, acc: any) =>
    total + (acc.holdings?.length || 0), 0
  );

  // Create source comparison data using dynamic values
  const sourceComparisons = [
    {
      field: 'Total Market Value',
      sourceA: `$${sourceAMarketValue.toLocaleString()}`,
      sourceB: `$${sourceBMarketValue.toLocaleString()}`,
      unifiedValue: `$${totalMarketValue.toLocaleString()}`,
      resolutionMethod: 'Aggregated'
    },
    {
      field: 'Client Identifier',
      sourceA: sourceAId,
      sourceB: sourceBId,
      unifiedValue: clientIdentifier,
      resolutionMethod: 'Mapped to unified ID'
    },
    {
      field: 'Account Count',
      sourceA: sourceAAccounts.length.toString(),
      sourceB: sourceBAccounts.length.toString(),
      unifiedValue: accountsCount.toString(),
      resolutionMethod: 'Aggregated'
    },
    {
      field: 'Holdings Count',
      sourceA: sourceAHoldings.toString(),
      sourceB: sourceBHoldings.toString(),
      unifiedValue: holdingsCount.toString(),
      resolutionMethod: 'Combined holdings'
    }
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground w-12"></th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Field</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{sourceAName}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{sourceBName}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Unified Value</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Resolution Method</th>
              </tr>
            </thead>
            <tbody>
              {sourceComparisons.map((comparison, index) => (
                <React.Fragment key={index}>
                  {/* Main row */}
                  <tr
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleRow(index)}
                  >
                    <td className="py-3 px-4 text-center">
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${
                          expandedRows[index] ? 'transform rotate-90' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </td>
                    <td className="py-3 px-4 text-sm">{comparison.field}</td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">{comparison.sourceA}</td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">{comparison.sourceB}</td>
                    <td className="py-3 px-4 text-sm">{comparison.unifiedValue}</td>
                    <td className="py-3 px-4 text-sm">
                      <Badge variant="secondary">{comparison.resolutionMethod}</Badge>
                    </td>
                  </tr>

                  {/* Expanded content */}
                  {expandedRows[index] && (
                    <tr>
                      <td colSpan={6} className="py-0 px-0">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-blue-200 px-8 py-6">
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Field Info */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Field</span>
                              </div>
                              <div className="text-base font-semibold text-gray-900">
                                {comparison.field}
                              </div>
                            </div>

                            {/* Unified Value */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Unified Value</span>
                              </div>
                              <div className="text-xl font-bold text-green-700">
                                {comparison.unifiedValue}
                              </div>
                            </div>

                            {/* Source Values */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Source Values</span>
                              </div>
                              <div className="space-y-1">
                                <div className="text-sm text-gray-700">
                                  <span className="font-semibold text-blue-600">{sourceAName}:</span>
                                  <span className="ml-1 font-medium">{comparison.sourceA}</span>
                                </div>
                                <div className="text-sm text-gray-700">
                                  <span className="font-semibold text-purple-600">{sourceBName}:</span>
                                  <span className="ml-1 font-medium">{comparison.sourceB}</span>
                                </div>
                              </div>
                            </div>

                            {/* Resolution Method */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Resolution Method</span>
                              </div>
                              <div>
                                <Badge variant="secondary" className="text-sm px-3 py-1">{comparison.resolutionMethod}</Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
